import { useState, useEffect, useMemo } from 'react'
import { Select } from 'antd'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import type { PracticeAgentReportData } from '../../types'
import { classifyColumns, pickLabelColumn, deriveChartDataForDimension, formatDateTimeLabel, formatFieldDisplayName } from '../../utils'
import { PaginationBar } from './PaginationBar'
import { productThemes } from '../../../../lib/productBranding'

// "Nice" tick values spanning [minVal, maxVal] — handles non-zero min and negative values.
function computeLineAxisTicks(minVal: number, maxVal: number, tickCount = 5): number[] {
  if (!Number.isFinite(minVal) || !Number.isFinite(maxVal)) return [0]
  const rawRange = maxVal - minVal
  if (rawRange === 0) {
    const offset = Math.abs(minVal) > 0 ? Math.abs(minVal) * 0.1 : 1
    return [minVal - offset, minVal, minVal + offset]
  }
  if (rawRange < 0) return [minVal, maxVal].sort((a, b) => a - b)
  const rawStep  = rawRange / (tickCount - 1)
  const mag      = Math.pow(10, Math.floor(Math.log10(rawStep)))
  const niceStep = Math.ceil(rawStep / mag) * mag
  const start    = Math.floor(minVal / niceStep) * niceStep
  const ticks: number[] = []
  for (let t = start; ticks.length <= tickCount + 2; t = parseFloat((t + niceStep).toFixed(10))) {
    if (t >= minVal - niceStep * 0.01) ticks.push(t)
    if (t > maxVal + niceStep * 0.01) break
  }
  return ticks
}

// Format a tick value: integer when whole, otherwise 1 decimal place.
function formatTickValue(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(1)
}

type WrappedTickOptions = {
  angle?: number
  maxLines?: number
  maxCharsPerLine?: number
  fontSize?: number
  lineHeight?: number
  color?: string
  margin?: {
    top?: number
    right?: number
    bottom?: number
    left?: number
  }
}

export function WrappedTickLabel(options?: WrappedTickOptions) {
  const {
    angle = -35,
    maxLines = 2,
    maxCharsPerLine = 10,
    fontSize = 11,
    lineHeight = 12,
    color = '#8e8e8e',
    margin = {},
  } = options || {}

  const {
    top = 0,
    right = 0,
    bottom = 0,
    left = 0,
  } = margin

  const getTextAnchor = () => {
    if (angle > 0) return 'start'
    if (angle < 0) return 'end'
    return 'middle'
  }

  type WrappedTickPayload = { value?: unknown }
  type WrappedTickProps = { x?: string | number; y?: string | number; payload?: WrappedTickPayload }

  return function WrappedTick(props: WrappedTickProps) {
    const { x, y, payload } = props

    // Apply margin offsets
    const adjustedX = Number(x ?? 0) + left - right
    const adjustedY = Number(y ?? 0) + top - bottom

    const words = formatDateTimeLabel(String(payload?.value || '')).split(' ')
    const lines: string[] = []

    let currentLine = ''

    for (const word of words) {
      if ((currentLine + word).length > maxCharsPerLine) {
        lines.push(currentLine.trim())
        currentLine = word + ' '
      } else {
        currentLine += word + ' '
      }
    }

    if (currentLine) lines.push(currentLine.trim())

    const finalLines = lines.slice(0, maxLines)

    if (lines.length > maxLines) {
      const lastIndex = maxLines - 1
      finalLines[lastIndex] = finalLines[lastIndex]?.replace(/\s+$/, '') + '...'
    }

    return (
      <g transform={`translate(${adjustedX},${adjustedY})`}>
        <text
          textAnchor={getTextAnchor()}
          fontSize={fontSize}
          fill={color}
          transform={`rotate(${angle})`}
        >
          {finalLines.map((line, index) => (
            <tspan key={index} x={0} dy={index === 0 ? 0 : lineHeight}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    )
  }
}

export function LineViz({ data, onSelect, onPageChange, onGroupByChange }: { data: PracticeAgentReportData; onSelect?: (field: string, value: string) => void; onPageChange?: (p: number) => void; onGroupByChange?: (dim: string) => void }) {
  const { dimensionCols } = useMemo(() => classifyColumns(data.columns, data.rows), [data.columns, data.rows])
  const availableDimensions = data.dimensions?.length ? data.dimensions : dimensionCols
  const defaultDimension = useMemo(
    () => data.groupByDimension ?? pickLabelColumn(dimensionCols, data.rows),
    [data.groupByDimension, dimensionCols, data.rows],
  )
  const [selectedDimension, setSelectedDimension] = useState<string | null>(defaultDimension)
  useEffect(() => { setSelectedDimension(defaultDimension) }, [defaultDimension])

  const chartData = useMemo(() => {
    if (!selectedDimension) return data.chartData
    const derived = deriveChartDataForDimension(data.columns, data.rows, selectedDimension)
    return derived.length > 0 ? derived : data.chartData
  }, [data.columns, data.rows, data.chartData, selectedDimension])

  const { yTicks, yDomainMin, yDomainMax } = useMemo(() => {
    if (chartData.length === 0) {
      return { yTicks: [0], yDomainMin: 0, yDomainMax: 0 }
    }
    const rawMin = Math.min(...chartData.map((d) => d.value))
    const rawMax = Math.max(...chartData.map((d) => d.value))
    const domainMin = data.axisMinValue ?? rawMin
    const domainMax = data.axisMaxValue ?? rawMax
    const ticks = computeLineAxisTicks(domainMin, domainMax)
    return {
      yTicks: ticks,
      yDomainMin: ticks[0] ?? domainMin,
      yDomainMax: ticks[ticks.length - 1] ?? domainMax,
    }
  }, [chartData, data.axisMinValue, data.axisMaxValue])

  if (chartData.length === 0) return <p className="text-sm text-gray-400 py-4">No chart data available.</p>
  return (
    <div>
      {availableDimensions.length > 1 && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs text-gray-500">Group by</span>
          <Select
            size="small"
            value={selectedDimension ?? undefined}
            onChange={(dim) => { setSelectedDimension(dim); onGroupByChange?.(dim) }}
            options={availableDimensions.map((col) => ({ value: col, label: formatFieldDisplayName(col) }))}
            style={{ minWidth: 140 }}
          />
        </div>
      )}
      <ResponsiveContainer width="100%" height={360}>
        <LineChart
          data={chartData}
          margin={{ top: 4, right: 8, left: 30, bottom: 30 }}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onClick={(chartEventData: any) => {
            const label = chartEventData?.activePayload?.[0]?.payload?.label
            if (label && onSelect) onSelect(selectedDimension ?? '', String(label))
          }}
          style={onSelect ? { cursor: 'pointer' } : undefined}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="label" tick={WrappedTickLabel({angle: -35, maxCharsPerLine: 20, maxLines: 2, margin: {top: 5}})} textAnchor="end" interval={0} height={60} />
          <YAxis
            tick={{ fontSize: 11 }}
            ticks={yTicks}
            tickFormatter={formatTickValue}
            domain={[yDomainMin, yDomainMax]}
          />
          <RechartsTooltip labelFormatter={(label) => formatDateTimeLabel(String(label ?? ''))} />
          <Line
            dataKey="value"
            stroke={productThemes.pracIntel.primary}
            strokeWidth={2}
            isAnimationActive={false}
            dot={{ fill: productThemes.pracIntel.primary, r: 4 }}
            activeDot={{ r: 6 }}
            name={formatFieldDisplayName(chartData[0]?.series?.[0]?.name ?? data.columns[1] ?? 'Value')}
          />
        </LineChart>
      </ResponsiveContainer>
      {onPageChange && data.totalRows != null && data.page != null && data.pageSize != null && (
        <PaginationBar
          page={data.page}
          totalRows={data.totalRows}
          pageSize={data.pageSize}
          onPageChange={onPageChange}
        />
      )}
    </div>
  )
}
